import React from 'react';
import DropdownInput from './DropdownInput';
import RadioInput from './RadioInput';
import CheckboxInput from './CheckboxInput';
import MultiCheckboxInput from './MultiCheckboxInput';
import SliderInput from './SliderInput';
import TextInput from './TextInput';
import NumberInput from './NumberInput';
import TextAreaInput from './TextAreaInput';
import DatePickerInput from './DatePickerInput';
import ColorPickerInput from './ColorPickerInput';
import ImageDisplay from './ImageDisplay';
import ImageCarousel from './ImageCarousel';
import ValueCards from './ValueCards';
import Swatches from './Swatches';
import { normalizeInputType } from '../../utils/featureNormalization';

/**
 * InputTypeComponent - Maps RuleEngine InputType to appropriate React component
 * 
 * This component bridges the gap between logic domain (RuleEngine.Api InputType)
 * and presentation domain (CaaS.Api GuiSchema presentation configs)
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {*} props.value - Current value
 * @param {Function} props.onChange - Change handler
 * @param {Object} props.presentationConfig - Presentation config from GuiSchema (optional)
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const InputTypeComponent = ({ 
    feature, 
    value, 
    onChange,
    presentationConfig = {},
    disabled = false,
    isOptionDisabled,
    className = ''
}) => {
    
    const inputType = normalizeInputType(feature?.property?.inputType || feature?.inputType).toLowerCase();
    
    // Extract presentation config for this specific feature
    const config = presentationConfig[feature?.name] || {};
    
    switch (inputType) {
        case 'dropdown':
            return (
                <DropdownInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    allowSearch={config.allowSearch}
                    showImages={config.showImages}
                    imageSize={config.imageSize}
                    isMultiSelect={config.isMultiSelect}
                    multiSelectMax={config.multiSelectMax}
                    placeholder={config.placeholder || 'Select an option...'}
                    disabled={disabled}
                    isOptionDisabled={isOptionDisabled}
                    className={className}
                />
            );
        
        case 'radio':
            return (
                <RadioInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    isOptionDisabled={isOptionDisabled}
                    className={className}
                />
            );
        
        case 'checkbox':
            return (
                <CheckboxInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    className={className}
                />
            );
        
        case 'multicheckbox':
            return (
                <MultiCheckboxInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    isOptionDisabled={isOptionDisabled}
                    className={className}
                />
            );
        
        case 'slider':
            return (
                <SliderInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    min={feature?.property?.minValue}
                    max={feature?.property?.maxValue}
                    step={feature?.property?.step || 1}
                    disabled={disabled}
                    className={className}
                />
            );
        
        case 'textinput':
            return (
                <TextInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    placeholder={config.placeholder || 'Enter text...'}
                    validationRegex={feature?.property?.validationRegex}
                    disabled={disabled}
                    className={className}
                />
            );
        
        case 'numberinput':
            return (
                <NumberInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    min={feature?.property?.minValue}
                    max={feature?.property?.maxValue}
                    step={feature?.property?.step || 1}
                    placeholder={config.placeholder || 'Enter number...'}
                    disabled={disabled}
                    className={className}
                />
            );
        
        case 'textarea':
            return (
                <TextAreaInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    placeholder={config.placeholder || 'Enter text...'}
                    rows={config.rows || 4}
                    disabled={disabled}
                    className={className}
                />
            );
        
        case 'datepicker':
            return (
                <DatePickerInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    format={config.dateFormat || 'YYYY-MM-DD'}
                    disabled={disabled}
                    className={className}
                />
            );
        
        case 'colorpicker':
            return (
                <ColorPickerInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    format={config.colorFormat || 'hex'}
                    disabled={disabled}
                    className={className}
                />
            );
        
        case 'imagedisplay':
            return (
                <ImageDisplay
                    feature={feature}
                    value={value}
                    size={config.imageSize || 'medium'}
                    className={className}
                />
            );

        case 'valuecards':
            return (
                <ValueCards
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    isOptionDisabled={isOptionDisabled}
                    className={className}
                />
            );

        case 'swatches':
            return (
                <Swatches
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    isOptionDisabled={isOptionDisabled}
                    className={className}
                />
            );

        case 'imagecarousel':
            return (
                <ImageCarousel
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    isOptionDisabled={isOptionDisabled}
                    className={className}
                />
            );
        
        default:
            // Fallback to dropdown for unknown types
            console.warn(`Unknown input type: ${inputType}, falling back to dropdown`);
            return (
                <DropdownInput
                    feature={feature}
                    value={value}
                    onChange={onChange}
                    placeholder={config.placeholder || 'Select an option...'}
                    disabled={disabled}
                    isOptionDisabled={isOptionDisabled}
                    className={className}
                />
            );
    }
};

export default InputTypeComponent;
